import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { TranscriptElement } from '../../types.ts/transcript';
import { observer } from 'mobx-react-lite';
import TranscriptElementView from './TranscriptElementView';
import TranscriptContextMenu from './transcript/TranscriptContextMenu';
import { useTranscriptStatus } from '../hooks';
import { useDemoStore } from '../EditorDemo';
import { VideoElement } from '../models/elements/VideoElement';

interface TranscriptPanelProps {
  originalTranscript?: ReadonlyArray<TranscriptElement>;
  activeTranscript?: TranscriptElement[];
  onWordClick?: (elementId: string, timelineTs?: number) => void;
  onRemoveSegment?: (
    segments: Array<{ elementId: string; initialIndexes: Array<number> }>,
  ) => void;
  onRestoreSegment?: (wordIds: string[]) => void;
  onReplaceWord?: (wordId: string, wordText: string) => void;
  onAttachPhoto?: (wordIds: string[]) => void;
  onCutWords?: (
    segments: Array<{ elementId: string; initialIndexes: Array<number> }>,
  ) => void;
  onPasteWords?: (
    time: number,
    startInitialIndex: number,
    endInitialIndex: number,
  ) => void;
  hasCutWords?: boolean;
  // No longer need demoStore prop as we use the context hook
}

/**
 * A panel for displaying and interacting with transcript elements
 */
const TranscriptPanel: React.FC<TranscriptPanelProps> = observer(
  ({
    originalTranscript,
    activeTranscript,
    onWordClick,
    onRemoveSegment,
    onRestoreSegment,
    onReplaceWord,
    onAttachPhoto,
    onCutWords,
    onPasteWords,
    hasCutWords = false,
    // demoStore is now accessed via hook
  }) => {
    // Get transcript status helper functions
    const { isElementActive, canWordsBeRemoved, canWordsBeRestored } =
      useTranscriptStatus();
    const demoStore = useDemoStore();

    const [viewMode, setViewMode] = useState<'original' | 'active'>('active');
    const [transcriptToDisplay, setTranscriptToDisplay] = useState<
      TranscriptElement[]
    >([]);
    const [selectedWordIds, setSelectedWordIds] = useState<string[]>([]);
    const [contextMenu, setContextMenu] = useState<{
      x: number;
      y: number;
      visible: boolean;
    }>({
      x: 0,
      y: 0,
      visible: false,
    });

    // For drag selection
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartIndex, setDragStartIndex] = useState<number | null>(null);

    // Update the displayed transcript based on the view mode and transcript changes
    useEffect(() => {
      if (viewMode === 'original' && originalTranscript) {
        console.log('Setting display to ORIGINAL transcript');
        setTranscriptToDisplay([...originalTranscript]);
      } else if (viewMode === 'active' && activeTranscript) {
        console.log('Setting display to ACTIVE transcript');
        setTranscriptToDisplay([...activeTranscript]);
      } else {
        console.log('Setting display to EMPTY array');
        setTranscriptToDisplay([]);
      }
    }, [viewMode, originalTranscript, activeTranscript]);

    const toggleViewMode = () => {
      // Cycle through view modes: active -> original -> active
      if (viewMode === 'active') {
        setViewMode('original');
      } else {
        setViewMode('active');
      }
    };

    // Get selected words from their IDs and determine if they can be removed/restored
    const getSelectedWords = (): TranscriptElement[] => {
      return transcriptToDisplay.filter(
        (word) =>
          word.type === 'text' &&
          word.word_index !== undefined &&
          selectedWordIds.includes(word.word_index.toString()),
      );
    };

    // Check if selected words can be removed
    const canRemoveSelectedWords = (): boolean => {
      if (selectedWordIds.length === 0) return false;

      // Use our hook to check if words can be removed
      // In active view: all words can be removed
      // In original view: only active words can be removed
      return canWordsBeRemoved(selectedWordIds, activeTranscript, viewMode);
    };

    // Check if selected words can be restored
    const canRestoreSelectedWords = (): boolean => {
      if (selectedWordIds.length === 0) return false;

      // For original view, use the hook
      if (!activeTranscript || !originalTranscript) return false;

      return canWordsBeRestored(
        selectedWordIds,
        originalTranscript,
        activeTranscript,
        viewMode,
      );
    };

    // Keep track of the last selected word for range selection
    const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);

    // Store context menu element IDs to avoid recalculating on every render
    const [contextMenuElementData, setContextMenuElementData] = useState<{
      containingElementId?: string;
      prevElementId: string | null;
      nextElementId: string | null;
    }>({
      containingElementId: undefined,
      prevElementId: null,
      nextElementId: null,
    });

    // Calculate element IDs when context menu becomes visible
    useEffect(() => {
      if (contextMenu.visible && demoStore) {
        const containingId = findContainingVideoElementId();
        const prevId = findPreviousVideoElementId();
        const nextId = findNextVideoElementId();

        setContextMenuElementData({
          containingElementId: containingId,
          prevElementId: prevId,
          nextElementId: nextId,
        });

        console.log('Context menu element IDs (computed once):', {
          containing: containingId,
          prev: prevId,
          next: nextId,
        });
      }
    }, [contextMenu.visible, demoStore, selectedWordIds]); // Update when context menu visibility, selection, or store changes

    // Handle word selection - only allow continuous ranges
    const handleWordSelect = (
      elementId: string,
      isMultiSelect: boolean,
      isShiftKey: boolean = false,
    ) => {
      // Find the index of the clicked element
      const clickedIndex = transcriptToDisplay.findIndex(
        (elem) =>
          elem.type === 'text' &&
          elem.word_index !== undefined &&
          elem.word_index.toString() === elementId,
      );

      if (clickedIndex === -1) return; // Skip if element not found

      if (isShiftKey && lastSelectedId && transcriptToDisplay.length > 0) {
        // Range selection with Shift key - this creates a continuous selection
        const lastIndex = transcriptToDisplay.findIndex(
          (elem) =>
            elem.type === 'text' &&
            elem.word_index !== undefined &&
            elem.word_index.toString() === lastSelectedId,
        );

        if (lastIndex !== -1) {
          // Get all elements between last selected and current
          const startIdx = Math.min(lastIndex, clickedIndex);
          const endIdx = Math.max(lastIndex, clickedIndex);

          const rangeIds = transcriptToDisplay
            .slice(startIdx, endIdx + 1)
            .filter(
              (elem) => elem.type === 'text' && elem.word_index !== undefined,
            )
            .map((elem) => elem.word_index!.toString());

          setSelectedWordIds(rangeIds);
        }
      } else if (isMultiSelect && selectedWordIds.length > 0) {
        // When multiselect (ctrl/cmd) but not shift:
        // Only allow extending the current selection if the clicked word is adjacent to the selection

        // Get actual indices of currently selected words
        const selectedIndices = selectedWordIds
          .map((id) =>
            transcriptToDisplay.findIndex(
              (elem) =>
                elem.type === 'text' &&
                elem.word_index !== undefined &&
                elem.word_index.toString() === id,
            ),
          )
          .filter((idx) => idx !== -1)
          .sort((a, b) => a - b);

        if (selectedIndices.length === 0) {
          // If no valid selection, just select the clicked word
          setSelectedWordIds([elementId]);
        } else {
          const minSelectedIdx = selectedIndices[0];
          const maxSelectedIdx = selectedIndices[selectedIndices.length - 1];

          // Allow selection if clicked word is adjacent to current selection
          if (
            clickedIndex === minSelectedIdx - 1 ||
            clickedIndex === maxSelectedIdx + 1
          ) {
            // Extend range
            const startIdx = Math.min(clickedIndex, minSelectedIdx);
            const endIdx = Math.max(clickedIndex, maxSelectedIdx);

            const rangeIds = transcriptToDisplay
              .slice(startIdx, endIdx + 1)
              .filter(
                (elem) => elem.type === 'text' && elem.word_index !== undefined,
              )
              .map((elem) => elem.word_index!.toString());

            setSelectedWordIds(rangeIds);
          } else if (
            clickedIndex >= minSelectedIdx &&
            clickedIndex <= maxSelectedIdx
          ) {
            // Clicked within the current range - do nothing
            return;
          } else {
            // Clicked outside the current range and not adjacent - start new selection
            setSelectedWordIds([elementId]);
          }
        }
      } else {
        // Replace current selection
        setSelectedWordIds([elementId]);
      }

      // Update the last selected ID
      setLastSelectedId(elementId);
    };

    // Handle right-click to open context menu
    const handleContextMenu = (
      e: React.MouseEvent<HTMLSpanElement>,
      elementId: string,
    ) => {
      e.preventDefault();

      // If the clicked word is not in the current selection, select only it
      if (!selectedWordIds.includes(elementId)) {
        setSelectedWordIds([elementId]);
        setLastSelectedId(elementId);
      }

      // Show context menu at exact click position
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        visible: true,
      });
    };

    // Close the context menu
    const closeContextMenu = () => {
      setContextMenu((prev) => ({ ...prev, visible: false }));
    };

    // Handle remove action from context menu
    const handleRemove = () => {
      console.log('Remove words:', selectedWordIds);
      if (selectedWordIds.length > 0 && onRemoveSegment) {
        // Get the containing element ID
        const containingId = contextMenuElementData.containingElementId;
        if (!containingId) {
          console.warn('No containing element found for removal');
          closeContextMenu();
          return;
        }

        // Get the selected words
        const selectedWords = getSelectedWords();
        if (selectedWords.length === 0) {
          console.warn('No selected words found for removal');
          closeContextMenu();
          return;
        }

        // Extract initial indexes
        const initialIndexes = selectedWords
          .filter(
            (word) => word.type === 'text' && word.initial_index !== undefined,
          )
          .map((word) => word.initial_index);

        if (initialIndexes.length === 0) {
          console.warn('No valid initial indexes found for removal');
          closeContextMenu();
          return;
        }

        // Format the segment object
        const segment = {
          elementId: containingId,
          initialIndexes,
        };

        console.log('Removing segment:', segment);
        onRemoveSegment([segment]);
      }
      closeContextMenu();
    };

    // Handle restore action from context menu
    const handleRestore = () => {
      console.log('Restore words:', selectedWordIds);

      // First check if we can use the new command approach
      if (selectedWordIds.length > 0 && demoStore?.editorStore?.editor) {
        try {
          // Get the selected elements
          const selectedWords = getSelectedWords();

          // Get the initial_index values from selected words
          const initialIndices = selectedWords
            .filter(
              (word) =>
                word.type === 'text' && word.initial_index !== undefined,
            )
            .map((word) => word.initial_index);

          // Skip if no valid initial indices
          if (initialIndices.length === 0) {
            console.warn(
              'No valid initial_index values found in selected words',
            );
            closeContextMenu();
            return;
          }

          // Log words being restored for debugging
          console.log(
            'Attempting to restore words:',
            selectedWords.map((w) => ({
              value: w.value,
              type: w.type,
              initial_index: w.initial_index,
              ts: w.type === 'text' ? w.ts : null,
            })),
          );
          console.log(
            'Using transcript words initial_index range:',
            initialIndices,
          );

          // Log positioning context
          console.log(
            'Positioning context - prevElementId:',
            contextMenuElementData.prevElementId,
            ', nextElementId:',
            contextMenuElementData.nextElementId,
          );

          // Find min and max to get range
          const startInitialIdx = Math.min(...initialIndices);
          const endInitialIdx = Math.max(...initialIndices);

          // Log the range
          console.log(
            `Using index range for restore: ${startInitialIdx}-${endInitialIdx}`,
          );

          // Extract prev and next element IDs from contextMenuElementData
          const { prevElementId, nextElementId } = contextMenuElementData;

          // Log info about neighboring elements
          const editor = demoStore.editorStore.editor;

          if (prevElementId) {
            const prevEl = editor.getElementById(prevElementId);
            if (prevEl) {
              console.log('Previous element:', {
                id: prevEl.id,
                time: prevEl.time,
                end_time: prevEl.end_time,
                trim_start: (prevEl as any).trim_start,
              });
            } else {
              console.warn(
                `Previous element with ID ${prevElementId} not found`,
              );
            }
          }

          if (nextElementId) {
            const nextEl = editor.getElementById(nextElementId);
            if (nextEl) {
              console.log('Next element:', {
                id: nextEl.id,
                time: nextEl.time,
                end_time: nextEl.end_time,
                trim_start: (nextEl as any).trim_start,
              });
            } else {
              console.warn(`Next element with ID ${nextElementId} not found`);
            }
          }

          // Execute the command directly through the editor
          const result = editor.commandTranscriptSimpleRestore(
            prevElementId ?? null,
            nextElementId ?? null,
            startInitialIdx,
            endInitialIdx,
          );

          console.log('TranscriptSimpleRestore execution result:', result);
        } catch (err) {
          console.error('Error executing TranscriptSimpleRestore:', err);
        }
      } else if (onRestoreSegment) {
        // Fall back to the original implementation if the new approach doesn't work
        onRestoreSegment(selectedWordIds);
      }

      closeContextMenu();
    };

    // Handle replace action from context menu
    const handleReplace = () => {
      if (selectedWordIds.length === 1 && onReplaceWord) {
        const selectedWord = getSelectedWords()[0];
        if (selectedWord && selectedWord.type === 'text') {
          console.log('Replace word:', selectedWordIds[0], selectedWord.value);
          onReplaceWord(selectedWordIds[0], selectedWord.value || '');
        }
      }
      closeContextMenu();
    };

    // Handle attach photo action from context menu
    const handleAttachPhoto = () => {
      if (selectedWordIds.length > 0 && onAttachPhoto) {
        console.log('Attach photo to words:', selectedWordIds);
        onAttachPhoto(selectedWordIds);
      }
      closeContextMenu();
    };

    // Handle cut action from context menu
    const handleCut = () => {
      if (selectedWordIds.length > 0 && onCutWords) {
        // Get the containing element ID
        const containingId = contextMenuElementData.containingElementId;
        if (!containingId) {
          console.warn('No containing element found for cut operation');
          closeContextMenu();
          return;
        }

        // Get the selected words
        const selectedWords = getSelectedWords();
        if (selectedWords.length === 0) {
          console.warn('No selected words found for cut operation');
          closeContextMenu();
          return;
        }

        // Extract initial indexes
        const initialIndexes = selectedWords
          .filter(
            (word) => word.type === 'text' && word.initial_index !== undefined,
          )
          .map((word) => word.initial_index);

        if (initialIndexes.length === 0) {
          console.warn('No valid initial indexes found for cut operation');
          closeContextMenu();
          return;
        }

        // Format the segment object
        const segment = {
          elementId: containingId,
          initialIndexes,
        };

        console.log('Cutting segment:', segment);
        onCutWords([segment]);
      }
      closeContextMenu();
    };

    // Handle paste action from context menu
    const handlePaste = () => {
      if (selectedWordIds.length === 1 && onPasteWords) {
        // Get the selected word
        const selectedWord = getSelectedWords()[0];
        if (selectedWord && selectedWord.type === 'text') {
          // Get the timestamp of the selected word, preferring timeline_ts
          const pasteTime =
            (selectedWord as any).timeline_ts ?? selectedWord.ts;

          if (pasteTime !== undefined && !isNaN(pasteTime)) {
            // Get the cut word IDs and calculate start/end initial index
            const cutWords =
              demoStore.editorStore.editor.getCutTranscriptInitialIndexes();
            if (cutWords.length > 0) {
              const initialIndices = cutWords;
              const startInitialIndex = Math.min(...initialIndices);
              const endInitialIndex = Math.max(...initialIndices);

              console.log(
                `Pasting at time ${pasteTime}s with indices ${startInitialIndex}-${endInitialIndex}`,
              );
              onPasteWords(pasteTime, startInitialIndex, endInitialIndex);
            }
          }
        }
      }
      closeContextMenu();
    };

    // Helper to find the video element containing the selected words based on transcript position
    const findContainingVideoElementId = (): string | undefined => {
      if (selectedWordIds.length === 0 || !activeTranscript) {
        console.log('findContainingVideoElementId: Not enough data', {
          hasSelectedWords: selectedWordIds.length > 0,
          hasActiveTranscript: !!activeTranscript,
        });
        return undefined;
      }

      const selectedWords = getSelectedWords();
      if (selectedWords.length === 0) {
        console.log('findContainingVideoElementId: No selected words found');
        return undefined;
      }

      // Get the words with initial_index values
      const wordsWithIndices = selectedWords.filter(
        (word) => word.type === 'text' && word.initial_index !== undefined,
      );

      if (wordsWithIndices.length === 0) {
        console.log(
          'findContainingVideoElementId: No words with initial_index found',
        );
        return undefined;
      }

      // Check if store is available
      if (!demoStore) {
        console.log('findContainingVideoElementId: No demo store available');
        return undefined;
      }

      try {
        const editor = demoStore.editorStore.editor;
        if (!editor) {
          console.log(
            'findContainingVideoElementId: Editor not available from demoStore',
          );
          return undefined;
        }

        // Get the transcript track and all video elements
        const transcriptTrack = editor.getTranscriptTrack();
        if (!transcriptTrack) {
          console.log(
            'findContainingVideoElementId: No transcript track found',
          );
          return undefined;
        }

        const videoElements = transcriptTrack.elements.filter(
          (el) => el.type === 'video',
        );

        console.log(
          'findContainingVideoElementId: Found video elements',
          videoElements.map((el) => ({
            id: el.id,
            time: el.time,
            end_time: el.end_time,
          })),
        );

        if (videoElements.length === 0) {
          console.log('findContainingVideoElementId: No video elements found');
          return undefined;
        }

        // For each selected word, check which video element contains it
        // We'll count the 'votes' for each element
        const elementVotes = new Map<string, number>();

        for (const word of wordsWithIndices) {
          if (word.type !== 'text' || word.initial_index === undefined)
            continue;

          // For each video element, check if it contains this word
          for (const element of videoElements) {
            // Manual implementation since getWordsForElement is not available
            const videoElement = element as VideoElement;
            const transcriptSegment = videoElement.getTranscriptSegment();
            if (!transcriptSegment) continue;

            const elementWords = transcriptSegment.getWordsWithTimestamps();
            if (!elementWords) continue;

            // Check if this element contains the current word
            const hasWord = elementWords.some(
              (w: TranscriptElement) =>
                w.type === 'text' && w.initial_index === word.initial_index,
            );

            if (hasWord) {
              // Increment vote count for this element
              const currentVotes = elementVotes.get(element.id) || 0;
              elementVotes.set(element.id, currentVotes + 1);
              break; // Move to next word, this one is found
            }
          }
        }

        // Find the element with the most votes
        let maxVotes = 0;
        let containingElementId: string | undefined = undefined;

        for (const [elementId, votes] of elementVotes.entries()) {
          if (votes > maxVotes) {
            maxVotes = votes;
            containingElementId = elementId;
          }
        }

        console.log(
          'findContainingVideoElementId: Element votes:',
          Array.from(elementVotes.entries()).map(([id, votes]) => ({
            id,
            votes,
          })),
        );

        if (containingElementId) {
          console.log(
            `findContainingVideoElementId: Found containing element: ${containingElementId} with ${maxVotes} matches`,
          );
          return containingElementId;
        } else {
          console.log(
            'findContainingVideoElementId: No matching element found for selected words',
          );
          return undefined;
        }
      } catch (err) {
        console.error('Error finding containing video element:', err);
        return undefined;
      }
    };

    // Helper to find the previous video element based on transcript position
    const findPreviousVideoElementId = (): string | null => {
      if (selectedWordIds.length === 0 || !originalTranscript) {
        console.log('findPreviousVideoElementId: Not enough data', {
          hasSelectedWords: selectedWordIds.length > 0,
          hasOriginalTranscript: !!originalTranscript,
        });
        return null;
      }

      // Safety check for demoStore
      if (!demoStore) {
        console.log('findPreviousVideoElementId: No demo store available');
        return null;
      }

      try {
        const selectedWords = getSelectedWords();
        if (selectedWords.length === 0) {
          console.log('findPreviousVideoElementId: No selected words found');
          return null;
        }

        // Find word with minimum initial_index for selection range
        const minIndexWord = selectedWords
          .filter(
            (word) => word.type === 'text' && word.initial_index !== undefined,
          )
          .reduce(
            (min, word) =>
              word.type === 'text' &&
              word.initial_index !== undefined &&
              (min === null || word.initial_index < min.initial_index)
                ? word
                : min,
            null as TranscriptElement | null,
          );

        if (
          !minIndexWord ||
          minIndexWord.type !== 'text' ||
          minIndexWord.initial_index === undefined
        ) {
          console.log(
            'findPreviousVideoElementId: No valid min index word found',
          );
          return null;
        }

        // Access editor through demoStore
        const editor = demoStore.editorStore.editor;
        if (!editor) {
          console.log(
            'findPreviousVideoElementId: Editor not available from demoStore',
          );
          return null;
        }

        // Get transcript track with existing video elements
        const transcriptTrack = editor.getTranscriptTrack();
        if (!transcriptTrack) {
          console.log('findPreviousVideoElementId: No transcript track found');
          return null;
        }

        // Get all video elements in the track
        const videoElements = transcriptTrack.elements
          .filter((el) => el.type === 'video')
          .sort((a, b) => a.time - b.time);

        console.log(
          'findPreviousVideoElementId: Found',
          videoElements.length,
          'video elements',
        );

        // Find the previous element based on transcript position instead of time
        // We'll use the ORIGINAL transcript to find word positions

        // First, find words that exist in the active transcript to the left of our selection
        // We need to search through the original transcript to find these positions
        const minInitialIndex = minIndexWord.initial_index;

        // Find all words in original transcript with smaller initial_index that are active
        const activeWords = originalTranscript.filter(
          (w) =>
            w.type === 'text' &&
            w.initial_index !== undefined &&
            w.initial_index < minInitialIndex &&
            activeTranscript?.some(
              (aw) =>
                aw.type === 'text' && aw.initial_index === w.initial_index,
            ),
        );

        if (activeWords.length === 0) {
          console.log(
            'findPreviousVideoElementId: No active words found before selection',
          );
          return null;
        }

        // Find maximum initial_index of active words to the left
        const maxLeftActiveWord = activeWords.reduce(
          (max, w) =>
            w.type === 'text' &&
            w.initial_index !== undefined &&
            (max === null || w.initial_index > max.initial_index)
              ? w
              : max,
          null as TranscriptElement | null,
        );

        if (!maxLeftActiveWord || maxLeftActiveWord.type !== 'text') {
          console.log(
            'findPreviousVideoElementId: No max left active word found',
          );
          return null;
        }

        console.log('findPreviousVideoElementId: Found max left active word', {
          value: maxLeftActiveWord.value,
          initial_index: maxLeftActiveWord.initial_index,
        });

        // Now we have the rightmost active word to the left of our selection
        // Find which video element contains this word
        for (const element of videoElements) {
          // Manual implementation since getWordsForElement is not available
          const videoElement = element as VideoElement;
          const transcriptSegment = videoElement.getTranscriptSegment();
          if (!transcriptSegment) continue;

          const elementWords = transcriptSegment.getWordsWithTimestamps();
          if (!elementWords) continue;

          // Check if the element contains our target word
          const hasWord = elementWords.some(
            (w: TranscriptElement) =>
              w.type === 'text' &&
              w.initial_index === maxLeftActiveWord.initial_index,
          );

          if (hasWord) {
            console.log(
              'findPreviousVideoElementId: Found element containing prev word',
              {
                id: element.id,
                time: element.time,
                word: maxLeftActiveWord.value,
              },
            );
            return element.id;
          }
        }

        console.log(
          'findPreviousVideoElementId: No element found containing prev word',
        );
        return null;
      } catch (err) {
        console.error('Error finding previous video element:', err);
        return null;
      }
    };

    // Helper to find the next video element based on transcript position
    const findNextVideoElementId = (): string | null => {
      if (selectedWordIds.length === 0 || !originalTranscript) {
        console.log('findNextVideoElementId: Not enough data', {
          hasSelectedWords: selectedWordIds.length > 0,
          hasOriginalTranscript: !!originalTranscript,
        });
        return null;
      }

      // Safety check for demoStore
      if (!demoStore) {
        console.log('findNextVideoElementId: No demo store available');
        return null;
      }

      try {
        const selectedWords = getSelectedWords();
        if (selectedWords.length === 0) {
          console.log('findNextVideoElementId: No selected words found');
          return null;
        }

        // Find word with maximum initial_index for selection range
        const maxIndexWord = selectedWords
          .filter(
            (word) => word.type === 'text' && word.initial_index !== undefined,
          )
          .reduce(
            (max, word) =>
              word.type === 'text' &&
              word.initial_index !== undefined &&
              (max === null || word.initial_index > max.initial_index)
                ? word
                : max,
            null as TranscriptElement | null,
          );

        if (
          !maxIndexWord ||
          maxIndexWord.type !== 'text' ||
          maxIndexWord.initial_index === undefined
        ) {
          console.log('findNextVideoElementId: No valid max index word found');
          return null;
        }

        // Access editor through demoStore
        const editor = demoStore.editorStore.editor;
        if (!editor) {
          console.log(
            'findNextVideoElementId: Editor not available from demoStore',
          );
          return null;
        }

        // Get transcript track with existing video elements
        const transcriptTrack = editor.getTranscriptTrack();
        if (!transcriptTrack) {
          console.log('findNextVideoElementId: No transcript track found');
          return null;
        }

        // Get all video elements in the track
        const videoElements = transcriptTrack.elements
          .filter((el) => el.type === 'video')
          .sort((a, b) => a.time - b.time);

        console.log(
          'findNextVideoElementId: Found',
          videoElements.length,
          'video elements',
        );

        // Find the next element based on transcript position instead of time
        // We'll use the ORIGINAL transcript to find word positions

        // First, find words that exist in the active transcript to the right of our selection
        // We need to search through the original transcript to find these positions
        const maxInitialIndex = maxIndexWord.initial_index;

        // Find all words in original transcript with larger initial_index that are active
        const activeWords = originalTranscript.filter(
          (w) =>
            w.type === 'text' &&
            w.initial_index !== undefined &&
            w.initial_index > maxInitialIndex &&
            activeTranscript?.some(
              (aw) =>
                aw.type === 'text' && aw.initial_index === w.initial_index,
            ),
        );

        if (activeWords.length === 0) {
          console.log(
            'findNextVideoElementId: No active words found after selection',
          );
          return null;
        }

        // Find minimum initial_index of active words to the right
        const minRightActiveWord = activeWords.reduce(
          (min, w) =>
            w.type === 'text' &&
            w.initial_index !== undefined &&
            (min === null || w.initial_index < min.initial_index)
              ? w
              : min,
          null as TranscriptElement | null,
        );

        if (!minRightActiveWord || minRightActiveWord.type !== 'text') {
          console.log('findNextVideoElementId: No min right active word found');
          return null;
        }

        console.log('findNextVideoElementId: Found min right active word', {
          value: minRightActiveWord.value,
          initial_index: minRightActiveWord.initial_index,
        });

        // Now we have the leftmost active word to the right of our selection
        // Find which video element contains this word
        for (const element of videoElements) {
          // Manual implementation since getWordsForElement is not available
          const videoElement = element as VideoElement;
          const transcriptSegment = videoElement.getTranscriptSegment();
          if (!transcriptSegment) continue;

          const elementWords = transcriptSegment.getWordsWithTimestamps();
          if (!elementWords) continue;

          // Check if the element contains our target word
          const hasWord = elementWords.some(
            (w: TranscriptElement) =>
              w.type === 'text' &&
              w.initial_index === minRightActiveWord.initial_index,
          );

          if (hasWord) {
            console.log(
              'findNextVideoElementId: Found element containing next word',
              {
                id: element.id,
                time: element.time,
                word: minRightActiveWord.value,
              },
            );
            return element.id;
          }
        }

        console.log(
          'findNextVideoElementId: No element found containing next word',
        );
        return null;
      } catch (err) {
        console.error('Error finding next video element:', err);
        return null;
      }
    };

    // Handle mouse down to start drag selection
    const handleMouseDown = (index: number, elementId: string) => {
      // Set drag start info
      setIsDragging(true);
      setDragStartIndex(index);

      // Start selection with this word
      setSelectedWordIds([elementId]);
      setLastSelectedId(elementId);
    };

    // Handle mouse move for drag selection
    const handleMouseMove = (index: number, elementId: string) => {
      if (!isDragging || dragStartIndex === null) return;

      // Calculate selection range
      const startIdx = Math.min(dragStartIndex, index);
      const endIdx = Math.max(dragStartIndex, index);

      // Get all text elements in the range
      const rangeIds = transcriptToDisplay
        .slice(startIdx, endIdx + 1)
        .filter((elem) => elem.type === 'text' && elem.word_index !== undefined)
        .map((elem) => elem.word_index!.toString());

      setSelectedWordIds(rangeIds);
    };

    // Handle mouse up to end drag selection
    const handleMouseUp = () => {
      setIsDragging(false);
      setDragStartIndex(null);
    };

    // Add mouse up handler to window to catch releases outside the component
    useEffect(() => {
      const handleWindowMouseUp = () => {
        if (isDragging) {
          setIsDragging(false);
          setDragStartIndex(null);
        }
      };

      window.addEventListener('mouseup', handleWindowMouseUp);
      return () => {
        window.removeEventListener('mouseup', handleWindowMouseUp);
      };
    }, [isDragging]);

    return (
      <TranscriptContainer>
        <TranscriptHeader>
          <h3>Transcript</h3>
          <TranscriptToggle onClick={toggleViewMode} viewMode={viewMode}>
            <span className={viewMode === 'active' ? 'active' : ''}>
              Active
            </span>
            <span className={viewMode === 'original' ? 'active' : ''}>
              Original
            </span>
            <Slider viewMode={viewMode} />
          </TranscriptToggle>
        </TranscriptHeader>

        <TranscriptContent>
          {transcriptToDisplay.length === 0 ? (
            <EmptyTranscript>
              {!originalTranscript ? (
                <p>No transcript loaded.</p>
              ) : (
                <p>No {viewMode} transcript available.</p>
              )}
            </EmptyTranscript>
          ) : (
            <TranscriptTextContainer onMouseUp={handleMouseUp}>
              {transcriptToDisplay.map((element, index) => (
                <TranscriptElementView
                  key={
                    element.type === 'text' && element.word_index !== undefined
                      ? `${element.word_index}-${index}`
                      : `${index}`
                  }
                  element={element}
                  isHighlighted={
                    element.type === 'text' &&
                    element.word_index !== undefined &&
                    element.word_index === demoStore.currentWordIndex
                  }
                  isCursorAfter={
                    element.type === 'text' &&
                    element.word_index !== undefined &&
                    element.word_index === (demoStore?.cursorWordIndex || -1)
                  }
                  showCursor={demoStore?.showCursor ?? true}
                  onClick={(e: React.MouseEvent<HTMLSpanElement>) => {
                    if (
                      element.type !== 'text' ||
                      element.word_index === undefined
                    )
                      return; // Only text elements with word_index are clickable

                    // For single click - only set cursor position, don't select
                    // Explicitly extract timeline_ts if it exists
                    const timelineTs =
                      'timeline_ts' in element
                        ? (element as any).timeline_ts
                        : element.ts; // Fall back to original ts if timeline_ts not available

                    // Pass word ID and timestamp to click handler for playhead positioning
                    onWordClick?.(element.word_index.toString(), timelineTs);

                    // Don't select word on single click, just set cursor
                    // Allow multi-select with Shift or Ctrl/Cmd key
                    const isMultiSelect = e.shiftKey || e.ctrlKey || e.metaKey;

                    if (isMultiSelect) {
                      // If multi-select keys are pressed, handle selection as before
                      handleWordSelect(
                        element.word_index.toString(),
                        true,
                        e.shiftKey,
                      );
                    } else {
                      // For single click without modifier keys, just clear selection
                      // This preserves cursor position without selecting the word
                      setSelectedWordIds([]);
                    }
                  }}
                  onDoubleClick={(e: React.MouseEvent<HTMLSpanElement>) => {
                    if (
                      element.type !== 'text' ||
                      element.word_index === undefined
                    )
                      return;

                    // Double-click selects the word
                    handleWordSelect(
                      element.word_index.toString(),
                      false,
                      false,
                    );
                  }}
                  onMouseDown={(e: React.MouseEvent<HTMLSpanElement>) => {
                    if (
                      element.type !== 'text' ||
                      element.word_index === undefined
                    )
                      return;
                    // Don't start drag on right click
                    if (e.button !== 0) return;
                    handleMouseDown(index, element.word_index.toString());
                  }}
                  onMouseEnter={() => {
                    if (
                      element.type !== 'text' ||
                      element.word_index === undefined
                    )
                      return;
                    handleMouseMove(index, element.word_index.toString());
                  }}
                  onContextMenu={(e: React.MouseEvent<HTMLSpanElement>) => {
                    if (
                      element.type === 'text' &&
                      element.word_index !== undefined
                    ) {
                      handleContextMenu(e, element.word_index.toString());
                    }
                  }}
                  isActive={
                    // In active view, all elements are shown as active
                    viewMode === 'active' ||
                    // In original view, show as active only if in the active transcript
                    (viewMode === 'original' &&
                      isElementActive(element, activeTranscript))
                  }
                  isSelected={
                    element.type === 'text' &&
                    element.word_index !== undefined &&
                    selectedWordIds.includes(element.word_index.toString())
                  }
                />
              ))}
            </TranscriptTextContainer>
          )}

          {/* Context Menu */}
          {contextMenu.visible ? (
            <TranscriptContextMenu
              x={contextMenu.x}
              y={contextMenu.y}
              selectedWords={getSelectedWords()}
              isActive={viewMode === 'active'}
              canRemove={canRemoveSelectedWords()}
              canRestore={canRestoreSelectedWords()}
              elementId={contextMenuElementData.containingElementId}
              prevElementId={contextMenuElementData.prevElementId}
              nextElementId={contextMenuElementData.nextElementId}
              onRemove={handleRemove}
              onRestore={handleRestore}
              onReplace={handleReplace}
              onAttachPhoto={onAttachPhoto ? handleAttachPhoto : undefined}
              onCut={onCutWords ? handleCut : undefined}
              onPaste={onPasteWords ? handlePaste : undefined}
              hasCutWords={hasCutWords}
              onClose={closeContextMenu}
            />
          ) : null}
        </TranscriptContent>
      </TranscriptContainer>
    );
  },
);

// Styled components
const TranscriptContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #171724;
  border-radius: 4px;
  width: 100%;
  flex: 1;
  min-height: 400px;
  margin-bottom: 16px;
`;

const TranscriptHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #333;

  h3 {
    margin: 0;
    font-size: 16px;
  }
`;

const TranscriptToggle = styled.div<{
  viewMode: 'active' | 'original';
}>`
  position: relative;
  display: flex;
  background-color: #2a2a3a;
  border-radius: 16px;
  height: 28px;
  padding: 2px;
  cursor: pointer;
  user-select: none;

  span {
    position: relative;
    z-index: 1;
    padding: 0 10px;
    display: flex;
    align-items: center;
    color: #666;
    transition: color 0.3s;
    font-size: 12px;
    flex: 1; // Make buttons equal width

    &.active {
      color: #fff;
    }
  }
`;

const Slider = styled.div<{
  viewMode: 'active' | 'original';
}>`
  position: absolute;
  left: ${(props) => {
    switch (props.viewMode) {
      case 'active':
        return '2px';
      case 'original':
        return 'calc(50% + 1px)';
      default:
        return '2px';
    }
  }};
  top: 2px;
  width: calc(50% - 2px);
  height: calc(100% - 4px);
  background-color: #1e3a8a;
  border-radius: 14px;
  transition: left 0.3s ease;
`;

const TranscriptContent = styled.div`
  flex: 1;
  padding: 16px;
  overflow-y: auto;
`;

const TranscriptTextContainer = styled.div`
  line-height: 1.6;
  font-size: 14px;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const EmptyTranscript = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
  font-style: italic;
`;

export default TranscriptPanel;
